import { motion } from 'framer-motion';
import { Button } from '@/components/Button';
import { useRouter } from 'next/router';
import {
  RocketLaunchIcon,
  GiftIcon,
  CogIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  PaintBrushIcon
} from '@heroicons/react/24/outline';
import { SEOMeta } from '@/templates/SEOMeta';
import { SeoInternalLinks } from "@/components/SeoInternalLinks";
import { useTranslations } from 'next-intl';
import { getI18nProps } from '@/utils/i18n';

export default function ReferralMarketing({ seoMeta }) {
  const router = useRouter();
  const t = useTranslations('referralMarketing');

  const features = [
    {
      icon: RocketLaunchIcon,
      title: t('features.fastSetup.title'),
      description: t('features.fastSetup.description')
    },
    {
      icon: GiftIcon,
      title: t('features.flexibleRewards.title'),
      description: t('features.flexibleRewards.description')
    },
    {
      icon: CogIcon,
      title: t('features.automation.title'),
      description: t('features.automation.description')
    },
    {
      icon: CurrencyDollarIcon,
      title: t('features.lowerCac.title'),
      description: t('features.lowerCac.description')
    },
    {
      icon: ChartBarIcon,
      title: t('features.analytics.title'),
      description: t('features.analytics.description')
    },
    {
      icon: UserGroupIcon,
      title: t('features.scalability.title'),
      description: t('features.scalability.description')
    },
    {
      icon: ShieldCheckIcon,
      title: t('features.privacy.title'),
      description: t('features.privacy.description')
    },
    {
      icon: PaintBrushIcon,
      title: t('features.branding.title'),
      description: t('features.branding.description')
    },
  ];

  const examples = [
    { name: t('examples.paypal.name'), stat: t('examples.paypal.stat') },
    { name: t('examples.tesla.name'), stat: t('examples.tesla.stat') },
    { name: t('examples.dropbox.name'), stat: t('examples.dropbox.stat') },
    { name: t('examples.americanGiant.name'), stat: t('examples.americanGiant.stat') },
    { name: t('examples.revolut.name'), stat: t('examples.revolut.stat') }
  ];

  return (
      <>
        <SEOMeta
            title={seoMeta?.title || t('seo.title')}
            description={seoMeta?.description || t('seo.description')}
            keywords={seoMeta?.keywords || t('seo.keywords')}
            img="/referral-marketing.png"
            url={seoMeta?.url || "/referral-marketing"}
        />

        <div className="bg-gradient-to-b from-gray-50 to-white">
          {/* Hero Section */}
          <section className="pt-28 pb-24 px-6">
            <div className="max-w-7xl mx-auto text-center">
              <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent mb-6"
              >
                {t('hero.title')}
              </motion.h1>

              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
                {t('hero.subtitle')}
              </p>

              <Button
                  onClick={() => router.push('/signup')}
                  className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-8 py-4 rounded-xl hover:opacity-90 transition-opacity"
              >
                {t('hero.cta')}
              </Button>
            </div>
          </section>

          {/* Image Section */}
          <section className="py-24">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">{t('image.title')}</h2>
              </div>

              <div className="rounded-xl overflow-hidden flex items-center justify-center">
                <img
                    src="/referral-marketing.png"
                    alt={t('image.alt')}
                    className="w-full max-w-4xl mx-auto"
                />
              </div>
            </div>
          </section>

          {/* CAC Section */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">{t('cac.title')}</h2>
                <p className="text-gray-600 max-w-3xl mx-auto">
                  {t('cac.subtitle')}
                </p>
              </div>

              <div className="items-center">
                <div>
                  <div className="items-center bg-gradient-to-r from-blue-600 to-green-500 text-white p-8 rounded-2xl mb-8">
                    <p className="items-center text-center text-3xl font-bold mb-2">
                      {t('cac.boxTitle')}
                    </p>
                    <p className="items-center text-center text-3xl font-bold mb-2">
                      {t('cac.boxValue')}
                    </p>
                  </div>

                  <p className="text-center text-lg mb-6">
                    <span className="font-bold">{t('cac.trustBold')}</span> {t('cac.trustText')}
                  </p>

                  <h3 className="text-center text-2xl font-semibold mb-4">
                    {t('cac.examplesTitle')}
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    {examples.map((example, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="bg-gray-50 p-4 rounded-lg"
                        >
                          <p className="font-bold">{example.name}</p>
                          <p className="text-blue-600">{example.stat}</p>
                        </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">{t('featuresSection.title')}</h2>
                <p className="text-gray-600 max-w-xl mx-auto">
                  {t('featuresSection.subtitle')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {features.map((feature, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="bg-white p-8 rounded-2xl shadow-sm"
                    >
                      <div className="mb-4">
                        <feature.icon className="w-12 h-12 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonial */}
          <section className="py-24 bg-white">
            <div className="max-w-4xl mx-auto px-6">
              <div className="space-y-6 text-lg">
                <p>{t('testimonial.p1')}</p>
                <p>{t('testimonial.p2')}</p>
                <p>{t('testimonial.p3')}</p>
                <p>{t('testimonial.p4')}</p>
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <section className="py-24">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <motion.div
                  initial={{ scale: 0.95 }}
                  whileInView={{ scale: 1 }}
                  className="bg-gradient-to-r from-blue-600 to-green-500 p-12 rounded-3xl text-white"
              >
                <h2 className="text-4xl font-bold mb-6">{t('finalCta.title')}</h2>
                <p className="text-xl mb-8 max-w-2xl mx-auto">
                  {t('finalCta.subtitle')}
                </p>
                <div className="flex justify-center">
                  <Button gray onClick={() => router.push('/signup')}>
                    {t('finalCta.cta')}
                  </Button>
                </div>
              </motion.div>
            </div>
          </section>

          <SeoInternalLinks />
        </div>
      </>
  );
}

export async function getStaticProps(ctx) {
  return getI18nProps(ctx);
}
